"use client";

import {RefObject, useEffect, useMemo, useRef, useState} from "react";
import {EVENT_COPY} from "../content";

/**
 * Every screen in the file, across both campaign stages:
 *
 *   boot      frames 1-4   progress bar, label advances
 *   intended  frame 5      the cyan nameplate
 *   launch    frames 6-8   rocket video, no content
 *   briefing  frames 8-9   (save-the-date) / 9-10 (formal invite)
 *   manifest  frame 11     crew intake form, formal invite only
 *   outcome   frames 12-13 cleared for launch / standing down
 */
export type MissionPhase = 'boot' | 'intended' | 'launch' | 'briefing' | 'manifest' | 'outcome';

/**
 * The fastest the boot sequence will ever run, with everything already cached.
 *
 * It is a floor, not a duration: the bar is gated on the page's media actually
 * arriving (see useMissionProgress), so a cold load holds here until the rocket
 * clip and the backdrops are down. A repeat visitor still gets the full 4.6s
 * rather than a bar that flashes past — the boot screen is branded content, not
 * an apology for a wait.
 */
export const BOOT_MS = 4600;

/**
 * The bar's hard ceiling, and the reason it can always be trusted to end.
 *
 * Gating on real downloads means a bad connection can hold the sequence, and
 * these invites open on show-floor wifi — nobody may ever sit in front of a
 * frozen bar. Past this point the bar finishes on time alone and the launch
 * takes its chances: it has a poster to show and its own backstop if the clip
 * never plays, which is survivable in a way that never arriving is not.
 *
 * It also doubles as the bar's slowest permitted pace, so even a load that has
 * stalled completely still shows movement.
 */
export const BOOT_MAX_MS = 15_000;

/**
 * How long the rocket has the screen to itself before the briefing fades in.
 *
 * The launch has to actually happen first. Timed off the clip: the rocket sits
 * on the pad building smoke for ~8s, lifts from ~9s, and its engines clear the
 * top of frame at ~13s. Cutting in before that put the invite on screen while
 * the rocket was still sitting there, which read as the video being background
 * texture rather than an event.
 *
 * The remaining ~2.5s of trailing exhaust plays under the copy as it arrives.
 */
export const LAUNCH_HOLD_MS = 13_500;

/**
 * Reduced motion still gets the launch, just long enough to read as a beat
 * rather than a cut. It sees the poster frame, not the moving clip.
 */
const REDUCED_LAUNCH_MS = 1200;

export const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** DD:HH:MM:SS remaining, or zeroes once the date has passed. */
export function useCountdown(target: string): string {
    const [now, setNow] = useState<number | null>(null);

    useEffect(() => {
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    return useMemo(() => {
        // null until mounted: the server and the first client render must agree,
        // otherwise the countdown hydrates with a different string every load.
        if (now === null) return '00:00:00:00';

        const diff = Math.max(0, new Date(target).getTime() - now);
        const pad = (n: number) => String(Math.floor(n)).padStart(2, '0');

        return [
            pad(diff / 86_400_000),
            pad((diff / 3_600_000) % 24),
            pad((diff / 60_000) % 60),
            pad((diff / 1000) % 60),
        ].join(':');
    }, [now, target]);
}

/**
 * Drives the boot bar while `active`, then calls `onComplete` once.
 *
 * `progress` is what the bar is measuring: a getter returning 0-1 for how much
 * of the page's media has arrived. The bar advances to whichever is LESS, the
 * design's pace or the real one, so the sequence cannot hand over to a launch
 * whose video has not downloaded — which is what used to happen, and which read
 * as the rocket clip being broken rather than late.
 *
 * Reduced motion advances the bar in the design's own discrete steps rather
 * than skipping it. Dropping the sequence outright — which is what this used to
 * do — removed the branded screen along with the animation, so those visitors
 * never saw the loader at all. The preference asks for less movement, not less
 * content.
 */
export function useBootProgress(active: boolean, progress: () => number, onComplete: () => void): {
    fillRef: RefObject<HTMLSpanElement | null>;
    label: string;
    valueNow: number;
} {
    // The bar's width is written straight to the node each frame instead of
    // going through state. Re-rendering the panel sixty times a second made the
    // fill visibly stutter, and none of the rest of the screen changes with it.
    const fillRef = useRef<HTMLSpanElement>(null);
    const [step, setStep] = useState(0);
    const started = useRef<number | null>(null);
    // Kept in a ref so a caller passing an inline arrow does not restart the bar
    // on every render.
    const complete = useRef(onComplete);
    complete.current = onComplete;
    // Same reasoning: read through a ref so a caller rebuilding the getter does
    // not restart the sequence.
    const read = useRef(progress);
    read.current = progress;

    const labels = EVENT_COPY.bootLabels;

    useEffect(() => {
        if (!active) return;

        let frame = 0;
        const tick = () => {
            started.current ??= performance.now();
            const elapsed = performance.now() - started.current;

            /*
             * Two floors and a ceiling, in one line.
             *
             * `min(pace, loaded)` is the honest part: a warm cache still takes
             * BOOT_MS, and a cold one waits for the bytes. `max(..., ceiling)`
             * is the safety net — it means the bar has a slowest permitted
             * speed, so a stalled download shows a bar still creeping rather
             * than one that has died, and the sequence always reaches the end.
             *
             * Every term only ever increases, so the fill can never run
             * backwards.
             */
            const ratio = Math.min(1, Math.max(
                Math.min(elapsed / BOOT_MS, read.current()),
                elapsed / BOOT_MAX_MS,
            ));

            if (fillRef.current !== null) {
                fillRef.current.style.width = `${ratio * 100}%`;
            }

            // Only the label crossing a boundary is worth a render — four times
            // over the whole sequence rather than every frame.
            const next = Math.min(labels.length - 1, Math.floor(ratio * labels.length));
            setStep(current => (current === next ? current : next));

            if (ratio >= 1) complete.current();
            else frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(frame);
    }, [active, labels.length]);

    return {
        fillRef,
        label: labels[step],
        // Coarse on purpose: a progressbar that announces a new value every
        // frame is worse for a screen reader than one that moves in steps.
        valueNow: Math.round((step / labels.length) * 100),
    };
}

/**
 * Holds the launch on screen for `LAUNCH_HOLD_MS`, then calls `onComplete`.
 *
 * Reduced motion gets a shorter hold on the poster frame rather than no launch
 * at all — same reasoning as the boot bar: the beat survives, the movement does
 * not.
 */
export function useLaunchHold(active: boolean, onComplete: () => void): void {
    const complete = useRef(onComplete);
    complete.current = onComplete;

    useEffect(() => {
        if (!active) return;

        const hold = prefersReducedMotion() ? REDUCED_LAUNCH_MS : LAUNCH_HOLD_MS;
        const timer = setTimeout(() => complete.current(), hold);
        return () => clearTimeout(timer);
    }, [active]);
}
